import type { Objective, ObjectivesMetadata } from '../types/milestones';
import { formatTimestampToLocal } from './dateUtils';

export type MilestoneUpdateInfo = {
  updatedAt: Date;
  updatedBy: string | null;
};

export type MilestonesFreshnessInput = {
  objectives: Objective[];
  metadata?: ObjectivesMetadata | null;
  metadataBySubawardee?: Record<string, ObjectivesMetadata | null>;
};

function readMetadataTimestamp(
  metadata: ObjectivesMetadata | null | undefined
): MilestoneUpdateInfo | null {
  if (!metadata?.updated_at) return null;

  const timestamp = new Date(metadata.updated_at).getTime();
  if (Number.isNaN(timestamp)) return null;

  return {
    updatedAt: new Date(timestamp),
    updatedBy: metadata.updated_by ?? null,
  };
}

function getSubawardeeIdsFromObjectives(objectives: Objective[]): string[] {
  const ids = new Set<string>();

  for (const objective of objectives) {
    if (objective.subawardee) ids.add(objective.subawardee);
    for (const keyActivity of objective.key_activities) {
      if (keyActivity.subawardee) ids.add(keyActivity.subawardee);
      for (const goal of keyActivity.goals) {
        for (const activity of goal.activities) {
          for (const milestone of activity.milestones) {
            if (milestone.subawardee) ids.add(milestone.subawardee);
          }
        }
      }
    }
  }

  return Array.from(ids);
}

function pickLatestUpdate(updates: MilestoneUpdateInfo[]): MilestoneUpdateInfo | null {
  return updates.reduce<MilestoneUpdateInfo | null>((latest, current) => {
    if (!latest || current.updatedAt.getTime() > latest.updatedAt.getTime()) {
      return current;
    }
    return latest;
  }, null);
}

/** Latest per-milestone status update in an objectives tree (legacy fallback). */
export function getLatestMilestoneUpdate(objectives: Objective[]): MilestoneUpdateInfo | null {
  const updates: MilestoneUpdateInfo[] = [];

  for (const objective of objectives) {
    for (const keyActivity of objective.key_activities) {
      for (const goal of keyActivity.goals) {
        for (const activity of goal.activities) {
          for (const milestone of activity.milestones) {
            if (!milestone.updated_at) continue;

            const timestamp = new Date(milestone.updated_at).getTime();
            if (Number.isNaN(timestamp)) continue;

            updates.push({
              updatedAt: new Date(timestamp),
              updatedBy: milestone.updated_by ?? null,
            });
          }
        }
      }
    }
  }

  return pickLatestUpdate(updates);
}

export function getLatestObjectivesUpdate(input: MilestonesFreshnessInput): MilestoneUpdateInfo | null {
  const { objectives, metadata, metadataBySubawardee } = input;

  if (metadataBySubawardee) {
    const scopedSubawardees = getSubawardeeIdsFromObjectives(objectives);
    const scopedUpdates = scopedSubawardees
      .map((subawardeeId) => readMetadataTimestamp(metadataBySubawardee[subawardeeId]))
      .filter((update): update is MilestoneUpdateInfo => update !== null);

    const latestScoped = pickLatestUpdate(scopedUpdates);
    if (latestScoped) return latestScoped;
  }

  const fromRoot = readMetadataTimestamp(metadata);
  if (fromRoot) return fromRoot;

  return getLatestMilestoneUpdate(objectives);
}

export function formatMilestonesLastUpdatedLabel(input: MilestonesFreshnessInput): string | null {
  const latest = getLatestObjectivesUpdate(input);
  if (latest) {
    const bySuffix = latest.updatedBy ? ` by ${latest.updatedBy}` : '';
    return `Last milestone update ${formatTimestampToLocal(latest.updatedAt)}${bySuffix}`;
  }

  return null;
}
